import axios from 'axios';
import { API_BASE_URL, handleApiError } from './_apiBase';
import { ApiMessage, ControlResult, PendingDevice, DeviceTrace, DeviceTraceResponse } from '../types/api';

export interface ZigbeeSettableProp {
    property: string;
    cluster: string;
    attribute: string;
    endpoint?: number;
    type: 'enum' | 'numeric' | 'boolean';
    description: string;
    unit?: string;
    min?: number;
    max?: number;
    values?: Array<{ value: number; label: string }>;
}

export interface ZigbeeReportableProp {
    property: string;
    cluster: string;
    attribute: string;
    description: string;
    unit?: string;
}

export interface ZigbeeCapabilities {
    deviceId: string;
    manufacturerName: string | null;
    modelId: string | null;
    endpoints: Array<{ id: number; inputClusters: string[]; outputClusters: string[] }>;
    settableProperties: ZigbeeSettableProp[];
    reportableProperties: ZigbeeReportableProp[];
}

/**
 * Fetch the settable/reportable capabilities for a Zigbee device
 * (derived from its ZCL clusters at runtime).
 */
export const fetchDeviceCapabilities = async (deviceId: string): Promise<ZigbeeCapabilities | null> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/devices/${encodeURIComponent(deviceId)}/capabilities`);
        return response.data;
    } catch (error: any) {
        if (error?.response?.status === 404) return null;
        return handleApiError(error, 'Hämta vad enheten klarar');
    }
};

/**
 * List the attributes the device reported as unsupported (UNSUPPORTED_ATTRIBUTE) and which the
 * coordinator therefore stopped offering/configuring.
 */
export const fetchUnsupportedAttributes = async (
    deviceId: string,
): Promise<Array<{ cluster: string; attribute: string }>> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/devices/${encodeURIComponent(deviceId)}/unsupported-attributes`);
        return response.data.entries ?? [];
    } catch (error) {
        return handleApiError(error, 'Hämta värden enheten inte stöder');
    }
};

/** Forget the learned unsupported-attribute set for a device so it is re-probed. */
export const clearUnsupportedAttributes = async (deviceId: string): Promise<{ success: boolean }> => {
    try {
        const response = await axios.delete(`${API_BASE_URL}/devices/${encodeURIComponent(deviceId)}/unsupported-attributes`);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Rensa värden enheten inte stöder');
    }
};

export interface ZigbeeDeviceHealth {
    deviceId: string;
    lqi: { min: number | null; avg: number | null; last: number | null; samples: number; windowHours: number };
    rejoins: { count: number; lastAt: string | null };
}

/** Connectivity health for a device: link-quality stats over a recent window + rejoin count. */
export const fetchDeviceHealth = async (deviceId: string): Promise<ZigbeeDeviceHealth | null> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/devices/${encodeURIComponent(deviceId)}/health`);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Hämta enhetens hälsa');
    }
};

/**
 * Read current ZCL attribute values from a Zigbee device over-the-air.
 */
export const readDeviceAttributes = async (
    deviceId: string,
    reads: Array<{ cluster: string; attribute: string; endpoint?: number }>,
): Promise<Array<{ cluster: string; attribute: string; value: any; status: 'ok' | 'queued' | 'error'; error?: string }>> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/devices/${encodeURIComponent(deviceId)}/read-attributes`, { reads });
        return response.data.results;
    } catch (error) {
        return handleApiError(error, 'Läsa av enhetens värden');
    }
};

/**
 * Read the current reporting configuration (min/max intervals, change threshold) for attributes.
 */
export const readReportingConfig = async (
    deviceId: string,
    reads: Array<{ cluster: string; attribute: string; endpoint?: number }>,
): Promise<Array<{ cluster: string; attribute: string; min?: number; max?: number; change?: number; status: 'ok' | 'queued' | 'error'; error?: string }>> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/devices/${encodeURIComponent(deviceId)}/read-reporting-config`, { reads });
        return response.data.results;
    } catch (error) {
        return handleApiError(error, 'Läsa av rapportinställningar');
    }
};

/**
 * Trigger a full Zigbee re-interview for a device to refresh its cluster
 * and configured-reporting setup (useful after re-pairing without factory reset).
 */
export const reInterviewDevice = async (deviceId: string): Promise<{ success: boolean; message: string }> => {
    const response = await axios.post(`${API_BASE_URL}/devices/${deviceId}/re-interview`);
    return response.data;
};

export const resetReportingConfig = async (deviceId: string): Promise<{ success: boolean; message: string }> => {
    const response = await axios.post(`${API_BASE_URL}/devices/${deviceId}/reset-reporting-config`);
    return response.data;
};

/**
 * Send OTA ZCL write / configureReporting commands to a Zigbee device.
 *   write     – [{ cluster, attributes }] attribute write commands
 *   reporting – [{ cluster, attribute, min, max, change }] reporting config commands
 */
export const configureDevice = async (
    deviceId: string,
    config: {
        endpoint?: number;
        write?: Array<{ cluster: string; attributes: Record<string, any> }>;
        reporting?: Array<{ cluster: string; attribute: string; min: number; max: number; change?: number }>;
    }
): Promise<any> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/devices/${deviceId}/configure`, config);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Ställa in enhet');
    }
};

/**
 * Fetch cached ZCL attribute values for a device.
 * These are populated automatically whenever the device sends attribute reports,
 * so this works even when the device is sleeping or a live read fails.
 */
export interface CachedAttributeEntry {
    cluster: string;
    attribute: string;
    value: any;
    updated_at: string; // ISO string
}

export const fetchAttributeCache = async (
    deviceId: string,
): Promise<CachedAttributeEntry[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/devices/${encodeURIComponent(deviceId)}/attribute-cache`);
        return response.data.entries ?? [];
    } catch {
        return [];
    }
};

export interface PendingQueueEntry {
    cluster: string;
    attribute: string;
    endpointNumber: number;
}

export interface PendingQueueConfigEntry extends PendingQueueEntry {
    min: number;
    max: number;
    change: number;
}

export interface PendingQueue {
    reads: PendingQueueEntry[];
    reportingReads: PendingQueueEntry[];
    reportingConfigs: PendingQueueConfigEntry[];
}

export const fetchPendingQueue = async (deviceId: string): Promise<PendingQueue> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/devices/${encodeURIComponent(deviceId)}/pending-queue`);
        return { reads: response.data.reads ?? [], reportingReads: response.data.reportingReads ?? [], reportingConfigs: response.data.reportingConfigs ?? [] };
    } catch {
        return { reads: [], reportingReads: [], reportingConfigs: [] };
    }
};

export interface ReportingConfigCacheEntry {
    cluster: string;
    attribute: string;
    min?: number;
    max?: number;
    change?: number;
}

export const fetchReportingConfigCache = async (deviceId: string): Promise<ReportingConfigCacheEntry[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/devices/${encodeURIComponent(deviceId)}/reporting-config-cache`);
        return response.data.entries ?? [];
    } catch {
        return [];
    }
};

/**
 * Control a single output channel on an IO module by its Zigbee endpoint number
 */
export const controlIoOutput = async (deviceId: string, endpoint: number, state: 'ON' | 'OFF'): Promise<ControlResult> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/devices/${deviceId}/control/io`, { endpoint, state });
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Styra IO-utgång');
    }
};

/**
 * Update device-specific settings (e.g. pulses_per_kwh for power meters)
 */
export const updateDeviceSettings = async (deviceId: string, settings: Record<string, any>): Promise<ApiMessage> => {
    try {
        const response = await axios.patch(`${API_BASE_URL}/devices/${deviceId}/settings`, settings);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Spara enhetens inställningar');
    }
};

export const startDeviceTrace = async (
    deviceId: string,
    config: { cluster?: string; property?: string; durationSeconds?: number; maxEntries?: number }
): Promise<DeviceTraceResponse> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/devices/${deviceId}/trace/start`, config);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Starta spårning');
    }
};

export const getDeviceTrace = async (deviceId: string): Promise<DeviceTrace> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/devices/${deviceId}/trace`);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Hämta spårning');
    }
};

export const stopDeviceTrace = async (deviceId: string): Promise<ApiMessage> => {
    try {
        const response = await axios.delete(`${API_BASE_URL}/devices/${deviceId}/trace`);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Stoppa spårning');
    }
};

export interface TopologyLink {
    source: string;
    target: string;
    lqi: number;
}

/** One Zigbee network — every coordinator has its own. */
export interface ZigbeeNetwork {
    gatewayId: string;
    gatewayName: string;
    coordinatorIeee: string | null;
    links: TopologyLink[];
    error?: string;
}

export interface NetworkTopology {
    networks: ZigbeeNetwork[];
}

export const fetchNetworkTopology = async (): Promise<NetworkTopology> => {
    try {
        // A scan asks each router in turn and can take ~40 s; the coordinators run in parallel.
        const response = await axios.get(`${API_BASE_URL}/devices/topology`, { timeout: 90000 });
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Hämta nätverkskarta');
    }
};

/**
 * Test connection to the Zigbee2MQTT coordinator
 */
export const testZigbeeConnection = async (): Promise<{ connected: boolean; message: string; endpoint: string }> => {

    try {
        await axios.get(`${API_BASE_URL}/bridge/info`, { timeout: 5000 });
        return {
            connected: true,
            message: 'Ansluten till SMLIGHT Zigbee-samordnare',
            endpoint: API_BASE_URL
        };
    } catch (error) {
        console.error('Connection test failed:', error);
        return {
            connected: false,
            message: `Failed to connect to ${API_BASE_URL}. Check if Zigbee2MQTT is running.`,
            endpoint: API_BASE_URL
        };
    }
};

export const fetchCoordinatorStatus = async (): Promise<boolean> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/devices/coordinator-status`);
        return response.data.connected === true;
    } catch {
        return false;
    }
};

/**
 * Enable pairing mode on the Zigbee coordinator
 * @param duration Duration in seconds (default: 60)
 */
export const enablePairingMode = async (duration: number = 60, gatewayId?: string): Promise<{ success: boolean; message: string; duration: number }> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/devices/permit-join`, { duration, ...(gatewayId ? { gatewayId } : {}) }, { timeout: 20000 });
        return {
            success: true,
            message: response.data.message || 'Parkopplingsläget är på',
            duration: response.data.duration || duration
        };
    } catch (error) {
        return handleApiError(error, 'Starta parkopplingsläget');
    }
};

export const disablePairingMode = async (gatewayId?: string): Promise<{ success: boolean; message: string }> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/devices/permit-join`, { duration: 0, ...(gatewayId ? { gatewayId } : {}) }, { timeout: 20000 });
        return { success: true, message: response.data.message || 'Parkopplingsläget är av' };
    } catch (error) {
        return handleApiError(error, 'Stänga av parkopplingsläget');
    }
};

/**
 * Control a light device (on/off/brightness)
 */
export const controlLight = async (deviceId: string, state: 'ON' | 'OFF', brightness?: number): Promise<ControlResult> => {
    try {
        const payload: { state: 'ON' | 'OFF'; brightness?: number } = { state };
        if (brightness !== undefined) {
            payload.brightness = brightness;
        }
        const response = await axios.post(`${API_BASE_URL}/devices/${deviceId}/control/light`, payload);
        return response.data;
    } catch (error) {
        return handleApiError(error, `Controlling light ${deviceId}`);
    }
};

/**
 * Control a switch/plug device (on/off)
 */
export const controlSwitch = async (deviceId: string, state: 'ON' | 'OFF'): Promise<ControlResult> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/devices/${deviceId}/control/switch`, { state });
        return response.data;
    } catch (error) {
        return handleApiError(error, `Controlling switch ${deviceId}`);
    }
};

/**
 * Set thermostat temperature
 */
export const setThermostat = async (deviceId: string, targetTemperature: number, mode?: string): Promise<ControlResult> => {
    try {
        const payload: { targetTemperature: number; mode?: string } = { targetTemperature };
        if (mode) {
            payload.mode = mode;
        }
        const response = await axios.post(`${API_BASE_URL}/devices/${deviceId}/control/thermostat`, payload);
        return response.data;
    } catch (error) {
        return handleApiError(error, `Setting thermostat ${deviceId}`);
    }
};

/**
 * Read sensor data (temperature, humidity, motion, contact)
 */
export const readSensorData = async (deviceId: string): Promise<Record<string, any>> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/devices/${deviceId}/sensor-data`);
        return response.data;
    } catch (error) {
        return handleApiError(error, `Reading sensor ${deviceId}`);
    }
};

/**
 * Get devices that have joined the network but are waiting for user confirmation
 */
export const fetchPendingDevices = async (): Promise<PendingDevice[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/devices/pending`);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Hämta enheter som väntar');
    }
};

/**
 * Confirm a pending device: give it a name + type so it gets saved and appears in the main list
 */
export const confirmPendingDevice = async (ieeeAddr: string, name: string, type: string): Promise<ApiMessage> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/devices/pending/${encodeURIComponent(ieeeAddr)}/confirm`, { name, type });
        return response.data;
    } catch (error) {
        return handleApiError(error, `Confirming device ${ieeeAddr}`);
    }
};

/**
 * Replace an existing device with a newly joined pending device.
 * All historical data is preserved (linked via devices.id).
 */
export const replaceDevice = async (pendingIeeeAddr: string, existingDeviceId: string): Promise<ApiMessage> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/devices/pending/${encodeURIComponent(pendingIeeeAddr)}/replace`, { existingDeviceId });
        return response.data;
    } catch (error) {
        return handleApiError(error, `Replacing device ${existingDeviceId} with ${pendingIeeeAddr}`);
    }
};

/**
 * Dismiss / reject a pending device
 */
export const dismissPendingDevice = async (ieeeAddr: string): Promise<ApiMessage> => {
    try {
        const response = await axios.delete(`${API_BASE_URL}/devices/pending/${encodeURIComponent(ieeeAddr)}`);
        return response.data;
    } catch (error) {
        return handleApiError(error, `Dismissing device ${ieeeAddr}`);
    }
};
